import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Instagram, Linkedin, Globe } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, StaggerGroup, staggerItem } from "./Reveal";
import { SOCIALS } from "../lib/constants";

export const About = () => {
  const { t } = useLang();
  const a = t.about;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  return (
    <section
      id="sobre"
      data-testid="about-section"
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Text column */}
          <div className="lg:col-span-7">
            <Reveal>
              <span className="font-mono-wt text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[#0047ff]">
                {a.overline}
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
                {a.title}
              </h2>
            </Reveal>

            <div className="mt-8 space-y-5 text-base md:text-lg font-light leading-relaxed text-zinc-400">
              <Reveal delay={0.1}><p>{a.p1}</p></Reveal>
              <Reveal delay={0.15}><p>{a.p2}</p></Reveal>
              <Reveal delay={0.2}><p>{a.p3}</p></Reveal>
            </div>

            <StaggerGroup className="mt-10 flex flex-wrap gap-3">
              {a.values.map((v) => (
                <motion.span
                  key={v}
                  variants={staggerItem}
                  className="rounded-full border border-white/10 bg-white/[0.02] px-4 py-2 text-sm text-zinc-300"
                >
                  {v}
                </motion.span>
              ))}
            </StaggerGroup>
          </div>

          {/* Founder column */}
          <div className="lg:col-span-5 relative" ref={ref}>
            <Reveal delay={0.1} className="relative">
              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0e]">
                <div className="absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 blue-glow opacity-80" />
                <motion.img
                  style={{ y: photoY }}
                  src={`${process.env.PUBLIC_URL}/images/jeferson-davi.jpeg`}
                  alt={a.founderName}
                  className="relative w-full h-[440px] sm:h-[520px] object-cover object-top grayscale-[15%]"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#050507] via-[#050507]/70 to-transparent p-8 pt-24">
                  <div className="font-display text-2xl md:text-3xl font-medium text-white">
                    {a.founderName}
                  </div>
                  <div className="mt-1 font-mono-wt text-sm uppercase tracking-[0.2em] text-[#00e5ff]">
                    {a.founderRole}
                  </div>
                  <div className="mt-1 text-sm text-zinc-400">{a.founderTag}</div>

                  <div className="mt-5 flex items-center gap-3">
                    <a
                      href={SOCIALS.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      data-testid="founder-instagram"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-300 hover:text-white hover:border-[#00e5ff]/50 transition-colors duration-300"
                    >
                      <Instagram className="h-4 w-4" />
                    </a>
                    <a
                      href={SOCIALS.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      data-testid="founder-linkedin"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-300 hover:text-white hover:border-[#00e5ff]/50 transition-colors duration-300"
                    >
                      <Linkedin className="h-4 w-4" />
                    </a>
                    <a
                      href="https://waltech.com.br/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Website"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-300 hover:text-white hover:border-[#00e5ff]/50 transition-colors duration-300"
                    >
                      <Globe className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
